<template>
  <ValidationObserver v-slot="{ invalid }">

    <Loader cover v-if="local.isProcessing"/>

    <card-subject-info :classSectionId="$route.params.classSectionId" currentPage="SUBMIT_SCORE"></card-subject-info>

    <!-- Upload Result Card -->
    <vx-card v-if="uploadResult.showCard" class="mb-4">
      <div slot="no-body" class="p-4">

        <div class="flex items-center justify-between mb-3">
          <p class="font-bold text-lg">{{ $t('student-score.upload-result') }}</p>
          <feather-icon icon="XIcon" svgClasses="w-5 h-5 cursor-pointer text-grey"
                        @click="uploadResult.showCard = false"/>
        </div>

        <!-- Summary counts -->
        <div class="vx-row mb-3">
          <div class="vx-col w-1/2 pr-1">
            <div class="flex items-center p-3 rounded" style="background: rgba(40,199,111,0.12)">
              <feather-icon icon="CheckCircleIcon" svgClasses="w-5 h-5 mr-2" class="text-success"/>
              <p class="font-bold text-success">
                {{ $t('student-score.upload-updated-count', { count: uploadResult.updatedCount }) }}
              </p>
            </div>
          </div>
          <div class="vx-col w-1/2 pl-1">
            <div class="flex items-center p-3 rounded" style="background: rgba(234,84,85,0.12)">
              <feather-icon icon="AlertCircleIcon" svgClasses="w-5 h-5 mr-2" class="text-danger"/>
              <p class="font-bold text-danger">
                {{ $t('student-score.upload-error-count', { count: uploadResult.errorDetails.length }) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Error detail table -->
        <div v-if="uploadResult.errorDetails && uploadResult.errorDetails.length > 0">
          <p class="font-semibold mb-2">{{ $t('student-score.upload-error-detail') }}</p>
          <div class="overflow-auto" style="max-height: 260px;">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b">
                  <th class="text-left p-2 whitespace-no-wrap">{{ $t('student-enroll.student-code') }}</th>
                  <th class="text-left p-2">{{ $t('general.error') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(err, idx) in uploadResult.errorDetails" :key="idx" class="border-b">
                  <td class="p-2 font-medium whitespace-no-wrap">{{ err.studentCode }}</td>
                  <td class="p-2">
                    <div v-for="(msg, mIdx) in err.errors" :key="mIdx" class="text-danger">
                      {{ msg }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </vx-card>

    <vx-card>

      <vs-alert color="warning" icon="icon-alert-circle" icon-pack="feather"
                class="mb-4">
        <p class="font-semibold">{{ $t('student-score.no-data-hint') }}</p>
        <p class="mt-1 font-semibold">{{ $t('student-score.no-data-hint-detail') }}</p>
      </vs-alert>

      <vs-table-mod :data="studentScores"
                    :max-items="tableConfig.maxItemsPerPage"
                    pagination
                    search
      >

        <template slot="header-left">
          <div class="flex items-center" style="gap: 8px;">
            <vs-button v-if="studentScores && studentScores.length > 0"
                       @click="handlerExportExcelClick"
                       color="primary" icon="icon-download" icon-pack="feather">
              {{ $t('general.excel') }}
            </vs-button>
            <template v-if="studentScores && studentScores.length > 0">
              <vs-button @click="$refs.excelUploadInput.click()"
                         color="warning" icon="icon-upload" icon-pack="feather">
                {{ $t('student-score.upload-score') }}
              </vs-button>
              <input ref="excelUploadInput" type="file" accept=".xlsx,.xls" class="hidden"
                     @change="handleExcelUpload"/>
            </template>
            <vs-button @click="onReCreateClick"
                       color="danger" icon="icon-refresh-cw" icon-pack="feather">
              {{ $t('student-score.re-create') }}
            </vs-button>
          </div>
        </template>

        <template slot="header">
          <vx-tooltip :text="$t('general.back')">
            <vs-button
              @click="onBackClick"
              class="ml-2" color="success" icon="icon-arrow-left" icon-pack="feather" radius
              type="filled"></vs-button>
          </vx-tooltip>
        </template>

        <template slot="thead">
          <vs-th v-for="(thead) in table.header" :key="thead.id"
                 :sort-key="thead.id"
                 :width="thead.width">
            {{ $t(thead.title) }}
          </vs-th>

          <vs-th v-for="(item, indexAssessment) in assessments" :key="indexAssessment">
            {{ item.nameLocal }}
          </vs-th>

          <vs-th>
            {{ $t('student-score.total-score') }}
          </vs-th>

          <vs-th>
            {{ $t('student-score.remark') }}
          </vs-th>

        </template>

        <template slot-scope="{data}">
          <vs-tr
            :data="{ data: tr, index: indextr }"
            :key="indextr"
            v-for="(tr, indextr) in data"
          >
<!--            <vs-td width="40">
              {{ indextr + 1 }}
            </vs-td>-->
            <vs-td>
              {{ tr.student.code }}
            </vs-td>
            <vs-td>
              <div class="flex items-center text-break">
                <vs-avatar :src="tr.student.picturePath ? tr.student.picturePath:''" class="flex-shrink-0 mr-2"
                           size="30px"
                />
                {{ tr.student | studentName }}
              </div>
            </vs-td>

            <vs-td v-for="(item, indexAssessment) in tr.studentScoreDetails" :key="indexAssessment">
              <div class="flex items-center">
                <ValidationProvider :name="$t('exam-upload.header.score')"
                                    :rules="{
                                    required: true
                                    , numeric: true
                                    , max_value: item.fullScore
                                    , min_value: 0
                                    }"
                                    v-slot="{ errors }">

                  <vs-input size="small" class="sm:w-full w-full"
                            :name="item.id"
                            :label="$t('student-score.full-score-short') + ' ' +item.fullScore"
                            min="0" :max="item.fullScore"
                            @input="onScoreChange(tr)"
                            v-model.number="item.score"/>
                  <span class="text-danger text-sm" v-show="errors[0]">{{errors[0]}}</span>
                </ValidationProvider>
              </div>
            </vs-td>

            <vs-td width="110" >
              <div class="flex items-center">
                {{ tr.totalScore }} /{{ tr.fullScore }}
                <vx-tooltip :title="$t('general.save')">
                  <feather-icon v-if="tr.isValueChanged"
                                class="ml-1 cursor-pointer"
                                @click.prevent="onSaveOneClick(tr, indextr)"
                                icon="SaveIcon"
                                svgClasses="w-5 h-5 text-primary stroke-current"/>
                </vx-tooltip>
              </div>
            </vs-td>

            <vs-td>
              <vs-input size="small" class="w-full"
                        @input="onRemarkChange(tr)"
                        v-model="tr.remark"/>
            </vs-td>

          </vs-tr>
        </template>
      </vs-table-mod>


    </vx-card>


    <!-- Actions Button -->
    <div class="vx-row pt-2">
      <div class="vx-col w-full">
        <vs-button :disabled="local.isProcessing || !isValueChanged || invalid"
                   @click.prevent="onSaveAllClick">
          {{ $t('general.save') }}
        </vs-button>

      </div>
    </div>

  </ValidationObserver>
</template>

<script>
  import XLSX from 'xlsx';
  import {state, template } from './data';
  import {tableConfig} from '@/utils/table'
  import {showStateNotification} from '@/utils/notification'
  import { APIServerPrisma2 } from "@/constants/config";
  import moment from 'moment';
  import {dateTimeFormat} from '@/utils/format';
  import {confirmSave} from "@/utils/dialogs";
  import * as _ from 'lodash';
  import CardSubjectInfo from "@/components/shared/card/CardSubjectInfo";

  import StudentScore from '@/store/modules/student-score/index';
  import CourseOutline from '@/store/modules/courses/course-outline/index';
  import {createHelpers} from 'vuex-map-fields';
  import {createNamespacedHelpers} from "vuex";

  if (!state.store.StudentScore) state.registerModule(`StudentScore`, StudentScore);
  if (!state.store.CourseOutline) state.registerModule(`CourseOutline`, CourseOutline);

  const {
    mapActions: mapStudentScoreActions,
  } = createNamespacedHelpers(`StudentScore`);
  const {
    mapActions: mapCourseOutlineActions,
  } = createNamespacedHelpers(`CourseOutline`);


  const {
    mapFields: mapStudentScoreFields,
  } = createHelpers({
    getterType: `StudentScore/getField`,
    mutationType: `StudentScore/updateField`,
  });
  const {
    mapFields: mapCourseOutlineFields,
  } = createHelpers({
    getterType: `CourseOutline/getField`,
    mutationType: `CourseOutline/updateField`,
  });


  export default {
    props:{
      classSectionId: {
        type: String,
        require: true,
      }
    },
    components: {
      Loader: () => import('@/components/common-ui/loader-block/index.vue'),
      vsTableMod: () => import('@/components/vuesax-mod/components/vsTable/vsTable.vue'),
      CardSubjectInfo,
    },
    data() {
      return {
        store: state.store,
        local: state.local,
        table: template.table,
        tableConfig: tableConfig,

        moment: moment,
        dateTimeFormat: dateTimeFormat,

        haveValueChange: false,

        uploadResult: {
          showCard: false,
          updatedCount: 0,
          errorDetails: [],   // [{ studentCode: string, errors: string[] }]
        },
      }
    },
    computed: {
      ...mapStudentScoreFields({
        studentScoreProcessing: 'processing',
        studentScoreSuccess: 'success',
        studentScoreError: 'error'
      }),
      ...mapCourseOutlineFields({courseOutlineProcessing: 'processing',}),
      studentScores() {
        return this.local.studentScores;
      },
      assessments() {
        return this.local.assessments;
      },
      isValueChanged(){
        return this.haveValueChange;
      },
    },
    watch: {
      courseOutlineProcessing(data) {
        this.local.isProcessing = data;
      },
      studentScoreProcessing(data) {
        this.local.isProcessing = data;
      },
      studentScoreSuccess(data) {
        showStateNotification(this.$vs, data);
      },
      studentScoreError: async function (data) {
        showStateNotification(this.$vs, data);
      },
    },
    methods: {
      //---------------------------------------- State Function ------------------------------------------------------//
      ...mapStudentScoreActions({
        getStudentScoresForTeacher: 'getStudentScoresForTeacher',
        updateStudentScore: 'updateStudentScore',
        updateStudentScoreArray: 'updateStudentScoreArray',
        reCreateStudentScore: 'reCreateStudentScore',
      }),
      ...mapCourseOutlineActions({
        getCourseOutline: 'getCourseOutline',
      }),
      //---------------------------------------- Event Function ------------------------------------------------------//
      async onSaveAllClick() {
        let updateItems = this.local.studentScores.filter(item => item.isValueChanged);
        if(updateItems && updateItems.length > 0){
          // check valid input
          // in case of ValidationObserver not working
          let isValid = true;
          for (let studentScore of updateItems){
            if(studentScore.studentScoreDetails && studentScore.studentScoreDetails.length > 0){

              let studentScoreDetail = studentScore.studentScoreDetails.find(item => !_.isNumber(item.score))
              if(studentScoreDetail){
                isValid = false;
                break;
              }
              //check score
              let isInvalidScore = studentScore.studentScoreDetails.find(item => item.score > item.fullScore)
              if(isInvalidScore){
                isValid = false;
                break;
              }
            }

            if(!isValid){
              break;
            }
          }

          if(isValid){
            confirmSave(this.$vs, this.saveAll, updateItems);
          }
        }
      },
      onBackClick(){
        this.$router.push({
          name: 'home',
        })
      },
      onReCreateClick() {
        this.$vs.dialog({
          type: 'confirm',
          color: 'danger',
          title: this.$t('student-score.re-create-confirm-title'),
          text: this.$t('student-score.re-create-confirm-message'),
          acceptText: this.$t('general.yes'),
          cancelText: this.$t('general.cancel'),
          accept: async () => {
            const result = await this.reCreateStudentScore({
              classSectionId: this.$route.params.classSectionId,
            });
            if (result !== null) {
              await this.loadData();
            }
          },
        });
      },
      async handlerExportExcelClick(){
        const rsc = JSON.parse(localStorage.getItem("rsc"));
        let entity, stage;
        if (rsc) {
          entity = rsc.e_code;
          stage = rsc.r_stage;
        }
        let url = APIServerPrisma2
          + '/api/export_excel_student_score_template'
          + '/' + this.$route.params.classSectionId
          + '/' + entity
          + '/' + stage;
        window.open(url, '_self');
      },

      // ─── Excel Upload ────────────────────────────────────────────────────────
      handleExcelUpload(e) {
        const file = e.target.files[0];
        if (!file) return;
        this.$refs.excelUploadInput.value = null; // allow re-selecting the same file
        this.parseExcelAndFillScores(file);
      },

      parseExcelAndFillScores(rawFile) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const workbook = XLSX.read(e.target.result, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            // header:1 → array-of-arrays; defval:'' → empty cells become ''
            const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

            // rows 0-4: subject info (skip)
            // row  5:   table header
            // row  6+:  student data
            if (!rows || rows.length < 7) {
              this.$vs.notify({
                title: this.$t('general.warning'),
                text: this.$t('student-score.upload-empty-file'),
                color: 'warning', iconPack: 'feather', icon: 'icon-alert-circle',
              });
              return;
            }

            const headerRow = rows[5];

            // Map column-index → assessmentIndex.
            // Backend appends " (10 คะแนน)" to each header — strip it before matching.
            const colToAssessmentIdx = {};
            headerRow.forEach((header, idx) => {
              if (idx === 0 || idx === 1) return; // col A = No (row number), col B = student-code — skip both
              const clean = String(header || '')
                .replace(/\s*\(\d+(\.\d+)?\s*คะแนน\)\s*$/, '')
                .trim();
              if (!clean) return;
              const aIdx = this.assessments.findIndex(a => a.nameLocal === clean);
              if (aIdx !== -1) colToAssessmentIdx[idx] = aIdx;
            });

            // Find "หมายเหตุ" column for remark
            let remarkColIdx = -1;
            headerRow.forEach((header, idx) => {
              if (String(header || '').trim() === 'หมายเหตุ') remarkColIdx = idx;
            });

            const updatedStudents = new Set();
            const errorMap = {};  // { [studentCode]: string[] }

            const newStudentScores = JSON.parse(JSON.stringify(this.local.studentScores));

            for (let i = 6; i < rows.length; i++) {
              const row = rows[i];
              if (!row || row.every(c => c === '' || c === null || c === undefined)) continue;

              const studentCode = String(row[1] || '').trim(); // col B = student code
              if (!studentCode) continue;

              const studentScoreIdx = newStudentScores.findIndex(s => s.student.code === studentCode);
              if (studentScoreIdx === -1) {
                errorMap[studentCode] = errorMap[studentCode] || [];
                errorMap[studentCode].push(this.$t('student-score.upload-student-not-found'));
                continue;
              }

              const studentScore = newStudentScores[studentScoreIdx];

              // ── Partial-row apply: each assessment column validated & applied independently ──
              Object.entries(colToAssessmentIdx).forEach(([colIdx, assessmentIdx]) => {
                const rawValue = row[parseInt(colIdx)];
                const detail   = studentScore.studentScoreDetails[assessmentIdx];
                if (!detail) return;

                // Blank cell → skip silently (don't overwrite existing score)
                if (rawValue === '' || rawValue === null || rawValue === undefined) return;

                const assessmentName = this.assessments[assessmentIdx].nameLocal;
                const numVal = Number(rawValue);

                if (isNaN(numVal) || !Number.isFinite(numVal)) {
                  errorMap[studentCode] = errorMap[studentCode] || [];
                  errorMap[studentCode].push(
                    `${assessmentName}: ${this.$t('student-score.upload-invalid-number')} (${rawValue})`
                  );
                  return;
                }
                if (numVal < 0) {
                  errorMap[studentCode] = errorMap[studentCode] || [];
                  errorMap[studentCode].push(
                    `${assessmentName}: ${this.$t('student-score.upload-below-min')}`
                  );
                  return;
                }
                if (numVal > detail.fullScore) {
                  errorMap[studentCode] = errorMap[studentCode] || [];
                  errorMap[studentCode].push(
                    `${assessmentName}: ${this.$t('student-score.upload-exceed-max')} (${numVal}/${detail.fullScore})`
                  );
                  return;
                }

                // ✅ Valid — apply this cell
                detail.score = numVal;
                updatedStudents.add(studentCode);
              });

              // Apply remark if the column exists in the file
              if (remarkColIdx !== -1) {
                const remarkValue = row[remarkColIdx];
                if (remarkValue !== '' && remarkValue !== null && remarkValue !== undefined) {
                  studentScore.remark = String(remarkValue).trim();
                  updatedStudents.add(studentCode);
                }
              }

              // Recalculate totalScore if at least one cell was applied for this student
              if (updatedStudents.has(studentCode)) {
                let total = 0;
                studentScore.studentScoreDetails.forEach(d => { total += d.score ? d.score : 0; });
                studentScore.totalScore = total;
                studentScore.isValueChanged = true;
              }
            }

            if (updatedStudents.size > 0) {
              this.local.studentScores = newStudentScores;
              this.haveValueChange = true;
            }

            this.uploadResult = {
              showCard: true,
              updatedCount: updatedStudents.size,
              errorDetails: Object.entries(errorMap).map(([studentCode, errors]) => ({ studentCode, errors })),
            };

          } catch (err) {
            this.$vs.notify({
              title: this.$t('general.error'),
              text: String(err.message || err),
              color: 'danger', iconPack: 'feather', icon: 'icon-alert-circle',
            });
          }
        };
        reader.readAsArrayBuffer(rawFile);
      },
      // ─────────────────────────────────────────────────────────────────────────

      onScoreChange(tr){

        tr.isValueChanged = true;
        this.haveValueChange = true;
        this.sumFullScore(tr);

      },
      onRemarkChange(tr) {
        tr.isValueChanged = true;
        this.haveValueChange = true;
      },
      async onSaveOneClick(tr, index){

        // check valid input
        // in case of ValidationObserver not working
        let hasCharacter = tr.studentScoreDetails.find(item => !_.isNumber(item.score))
        //check score
        let isInvalid = tr.studentScoreDetails.find(item => item.score > item.fullScore)
        if(!isInvalid && !hasCharacter){

          let studentScoreDetailsInput = [];
          tr.studentScoreDetails.forEach(item => {
            studentScoreDetailsInput.push({id: item.id, score: item.score});
          });

          let payload = {
            id: tr.id,
            studentScoreInput: {
              totalScore: tr.totalScore,
              remark: tr.remark,
            },
            studentScoreDetailsInput: studentScoreDetailsInput,
          };

          let result = await this.updateStudentScore(payload);

          if(result){
            //update list
            result = JSON.parse(JSON.stringify(result));
            result['isValueChanged'] = false;

            let newList = JSON.parse(JSON.stringify(this.local.studentScores));
            newList[index] = result;
            this.local.studentScores = newList;
          }
        }

      },
      //---------------------------------------- Local Function ------------------------------------------------------//
      async saveAll(updateItems){
        let payload = []
        updateItems.forEach(item =>{

          let studentScoreDetailsInput = []
          if(item.studentScoreDetails && item.studentScoreDetails.length > 0){
            item.studentScoreDetails.forEach(innerItem => {
              studentScoreDetailsInput.push({id: innerItem.id, score: innerItem.score});
            })
          }

          payload.push({
              id: item.id,
              studentScoreInput: {
                totalScore: item.totalScore,
                remark: item.remark,
              },
              studentScoreDetailsInput: studentScoreDetailsInput
          });

        })
        let result = await this.updateStudentScoreArray({studentScoreArrayInput: payload});

        if(result){
          result = JSON.parse(JSON.stringify(result));
          this.local.studentScores = result;
        }
      },
      sumFullScore(tr){
        let totalScore = 0;
        if(tr.studentScoreDetails && tr.studentScoreDetails.length > 0){
          tr.studentScoreDetails.forEach(item => {
            totalScore += item.score ? item.score:0;
          });
        }
        tr.totalScore = totalScore;
      },
      async loadData() {
        let payload = {
          studentScoreFilterInput:{
            classSectionId: this.$route.params.classSectionId,
          }
        };
        let result = await this.getStudentScoresForTeacher(payload);

        if(result){
          result = JSON.parse(JSON.stringify(result));

          if (result.length === 0) {
            this.local.studentScores = [];
            this.local.assessments = [];
            return;
          }

          //get courseOutline Assessment
          let courseOutline = result[0].studentEnrollDetail.courseOutlineRegistration.courseOutline
          let courseOutlineAssessment = await this.getCourseOutline({id: courseOutline.id})

          if(courseOutlineAssessment){
            courseOutlineAssessment = JSON.parse(JSON.stringify(courseOutlineAssessment));
            this.local.assessments = courseOutlineAssessment.courseOutlineAssessments.map(item => {
              return {...item.assessment}
            });
          }else{
            this.local.assessments = [];
          }

          this.local.studentScores = result.map(item => {
            return {
              ...item,
              isValueChanged: false,
            }
          });
        }
      },
    },
    async mounted() {
      await this.loadData();
    }
  }
</script>

<style scoped>

</style>

